import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AppContext';
import Modal from '../../components/common/Modal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import toast from 'react-hot-toast';
import { Plus, Pencil, Trash2, Lock } from 'lucide-react';

// These 4 are system defaults — perm_key cannot be changed, delete blocked in UI
const SYSTEM_KEYS = ['can_view', 'can_create', 'can_edit', 'can_delete'];

const initForm = { perm_key:'', label:'', description:'', sort_order:0, is_active:1 };

export default function PermissionTypes() {
  const { api } = useAuth();
  const [types,   setTypes]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal,   setModal]   = useState(false);
  const [form,    setForm]    = useState(initForm);
  const [editId,  setEditId]  = useState(null);
  const [saving,  setSaving]  = useState(false);
  const [delId,   setDelId]   = useState(null);
  const [delLoad, setDelLoad] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/superadmin/permission-types');
      setTypes(res.data);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm(initForm); setEditId(null); setModal(true); };

  const openEdit = t => {
    setForm({ perm_key: t.perm_key, label: t.label, description: t.description||'', sort_order: t.sort_order, is_active: t.is_active });
    setEditId(t.id);
    setModal(true);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editId) {
        await api.put(`/superadmin/permission-types/${editId}`, form);
        toast.success('Permission type updated');
      } else {
        await api.post('/superadmin/permission-types', form);
        toast.success('Permission type created');
      }
      setModal(false); load();
    } catch (err) { toast.error(err?.response?.data?.message || 'Error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setDelLoad(true);
    try {
      await api.delete(`/superadmin/permission-types/${delId}`);
      toast.success('Deleted'); setDelId(null); load();
    } catch (err) { toast.error(err?.response?.data?.message || 'Cannot delete — permission type may be in use'); }
    finally { setDelLoad(false); }
  };

  const isSystem = key => SYSTEM_KEYS.includes(key);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Permission Types</h1>
          <p className="page-subtitle">
            Define custom permission actions (View, Create, Edit, Delete, Export…) used across all roles and modules.
          </p>
        </div>
        <button className="btn btn-primary" onClick={openCreate}><Plus size={15}/> New Permission Type</button>
      </div>

      {loading ? (
        <div className="flex-center" style={{ padding:60 }}><span className="spinner" style={{ width:32,height:32 }}/></div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Key</th>
                <th>Label</th>
                <th>Description</th>
                <th style={{textAlign:'center'}}>Sort</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {types.map(t => (
                <tr key={t.id}>
                  <td>
                    <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                      <code style={{ background:'var(--bg-input)', padding:'2px 7px', borderRadius:4, fontSize:'.78rem', fontFamily:'monospace' }}>
                        {t.perm_key}
                      </code>
                      {isSystem(t.perm_key) && (
                        <span className="badge badge-panel" style={{ fontSize:'.65rem', padding:'1px 5px' }}>System</span>
                      )}
                    </div>
                  </td>
                  <td style={{ fontWeight:600 }}>{t.label}</td>
                  <td className="text-muted text-sm">{t.description || '—'}</td>
                  <td style={{ textAlign:'center', color:'var(--text-muted)', fontSize:'.85rem' }}>{t.sort_order}</td>
                  <td>
                    <span className={`badge ${t.is_active ? 'badge-active' : 'badge-inactive'}`}>
                      {t.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="flex gap-2">
                      <button className="btn btn-ghost btn-sm btn-icon" title="Edit" onClick={() => openEdit(t)}>
                        <Pencil size={14}/>
                      </button>
                      {!isSystem(t.perm_key) && (
                        <button className="btn btn-ghost btn-sm btn-icon" title="Delete"
                          style={{ color:'var(--danger)' }} onClick={() => setDelId(t.id)}>
                          <Trash2 size={14}/>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {!types.length && (
                <tr><td colSpan={6}>
                  <div className="empty-state">
                    <Lock size={36}/>
                    <h3>No permission types</h3>
                    <p>Create custom permission types to extend beyond the defaults.</p>
                  </div>
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Create / Edit Modal */}
      <Modal open={modal} onClose={() => setModal(false)} title={editId ? 'Edit Permission Type' : 'New Permission Type'}>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Permission Key <span className="req">*</span></label>
              <input
                className="form-input"
                placeholder="e.g. can_export"
                value={form.perm_key}
                onChange={e => setForm(f => ({ ...f, perm_key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g,'') }))}
                required
                disabled={!!editId}
              />
              <div style={{ fontSize:'.72rem', color:'var(--text-muted)', marginTop:4 }}>
                Unique snake_case key. Cannot be changed after creation.
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Label <span className="req">*</span></label>
              <input className="form-input" placeholder="e.g. Export"
                value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))} required />
            </div>
            <div className="form-group">
              <label className="form-label">Description</label>
              <input className="form-input" placeholder="What does this permission allow?"
                value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Sort Order</label>
                <input className="form-input" type="number" min={0}
                  value={form.sort_order} onChange={e => setForm(f => ({ ...f, sort_order: Number(e.target.value) }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Status</label>
                <select className="form-select" value={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: +e.target.value }))}>
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </select>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? <span className="spinner"/> : editId ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={!!delId}
        onClose={() => setDelId(null)}
        onConfirm={handleDelete}
        loading={delLoad}
        title="Delete Permission Type"
        message="This will remove this permission type from all roles and employees. This cannot be undone."
      />
    </div>
  );
}
